/**
 * lineItemWizard — optional block inside object `config` (JSON) for opportunity, quote, and order.
 *
 * Example (store on the parent object: opportunity, quote, or order):
 * {
 *   "lineItemWizard": {
 *     "strings": {
 *       "modalTitle": "Add services",
 *       "stepSelect": "1. Select services",
 *       "stepEdit": "2. Review lines",
 *       "searchResultsHeading": "Search results",
 *       "selectedHeading": "Selected",
 *       "editHeading": "Edit lines",
 *       "searchPlaceholder": "Search by name, code, or category",
 *       "noSearchResults": "No items match the current search.",
 *       "emptySelected": "No items selected yet.",
 *       "footerSelect": "Select items and click Next to review.",
 *       "footerEdit": "Adjust quantities and prices before saving.",
 *       "backButton": "Back to add"
 *     },
 *     "searchColumns": {
 *       "productName": { "visible": true, "label": "Service name" },
 *       "productCode": { "visible": true, "label": "Code" },
 *       "productCategory": { "visible": true, "label": "Category" },
 *       "listPrice": { "visible": true, "label": "List price" },
 *       "action": { "visible": true, "label": "Action" }
 *     },
 *     "editColumns": {
 *       "productName": { "visible": true, "label": "Service" },
 *       "productCode": { "visible": true, "label": "Code" },
 *       "productCategory": { "visible": true, "label": "Category" },
 *       "quantity": { "visible": true, "label": "Quantity" },
 *       "listPrice": { "visible": true, "label": "List price" },
 *       "salesPrice": { "visible": true, "label": "Sales price" },
 *       "discountPercent": { "visible": true, "label": "Discount %" },
 *       "lineTotal": { "visible": true, "label": "Line total" },
 *       "remove": { "visible": true, "label": "Remove" }
 *     },
 *     "searchColumnOrder": ["productName", "listPrice", "action"],
 *     "editColumnOrder": ["productName", "quantity", "lineTotal"]
 *   }
 * }
 *
 * Column membership and column order come from searchColumnOrder and editColumnOrder (keys listed = shown, in order).
 * Line quantity is clamped using the parent product’s `min_quantity` / `max_quantity` (API: `minQuantity` / `maxQuantity` on search and line payloads).
 *
 * **Single profile:** use `config.lineItemWizard` only. Older tenants may still have `lineItemWizards.products` / `lineItemWizards.services`;
 * the app reads those only as migration fallbacks when top-level `lineItemWizard` is absent.
 */


/** Default columns bundled with the add-products wizard (IDs are stable keys in saved JSON). */
export const SEARCH_BUILTIN_KEYS = ['productName', 'productCode', 'productCategory', 'listPrice', 'action'];
export const EDIT_BUILTIN_KEYS = [
  'productName',
  'productCode',
  'productCategory',
  'quantity',
  'listPrice',
  'salesPrice',
  'discountPercent',
  'lineTotal',
  'remove',
];

/** Backward-compatible aliases (same arrays as *_BUILTIN_*). */
export const SEARCH_KEYS = SEARCH_BUILTIN_KEYS;
export const EDIT_KEYS = EDIT_BUILTIN_KEYS;

export const DEFAULT_SEARCH_LABELS = {
  productName: 'Product Name',
  productCode: 'Code',
  productCategory: 'Category',
  listPrice: 'List Price',
  action: 'Action',
};

export const DEFAULT_EDIT_LABELS = {
  productName: 'Product',
  productCode: 'Code',
  productCategory: 'Category',
  quantity: 'Quantity',
  listPrice: 'List Price',
  salesPrice: 'Sales Price',
  discountPercent: 'Discount %',
  lineTotal: 'Line Total',
  remove: 'Remove',
};

/**
 * Database / API field name for a line-item wizard column (Product catalog or quote/opportunity line table).
 * Built-in camelCase keys map to snake_case columns; extra Product fields use `field_name` as the key.
 * @returns {string|null} snake_case API name, or `null` for UI-only columns (action, remove)
 */
export function getLineItemWizardColumnApiName(key) {
  if (key == null || key === '') {
    return '';
  }
  const k = String(key);
  const builtins = {
    productName: 'name',
    productCode: 'product_code',
    productCategory: 'product_category',
    listPrice: 'list_price',
    quantity: 'quantity',
    salesPrice: 'sales_price',
    discountPercent: 'discount',
    lineTotal: 'total_price',
    action: null,
    remove: null,
  };
  if (Object.prototype.hasOwnProperty.call(builtins, k)) {
    return builtins[k];
  }
  return k;
}

export function humanizeLineItemKey(key) {
  if (key == null || key === '') {
    return '';
  }
  const s = String(key);
  return s
    .replace(/__/g, ' — ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function uniqKeyList(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    if (!Array.isArray(list)) {
      continue;
    }
    for (const k of list) {
      if (typeof k !== 'string' || k.trim() === '') {
        continue;
      }
      const t = k.trim();
      if (seen.has(t)) {
        continue;
      }
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

/**
 * @param {object} [rawLineItemWizard]
 * @param {string[]} [productFieldNames] field_name values from the Product object definition
 */
export function collectLineItemSearchUnionKeys(rawLineItemWizard = {}, productFieldNames = []) {
  const partial = rawLineItemWizard.searchColumns || rawLineItemWizard.searchTable?.columns;
  const partialKeys =
    partial && typeof partial === 'object'
      ? Object.keys(partial).filter((k) => partial[k] != null && typeof partial[k] === 'object')
      : [];
  const order =
    rawLineItemWizard.searchColumnOrder ||
    rawLineItemWizard.searchColumnsOrder ||
    (Array.isArray(rawLineItemWizard.search_columns_order) ? rawLineItemWizard.search_columns_order : null);
  const orderKeys = Array.isArray(order) ? order : [];
  const pf = Array.isArray(productFieldNames) ? productFieldNames.map((x) => String(x).trim()).filter(Boolean) : [];
  return uniqKeyList(SEARCH_BUILTIN_KEYS, pf, partialKeys, orderKeys);
}

/** @see collectLineItemSearchUnionKeys */
export function collectLineItemEditUnionKeys(rawLineItemWizard = {}, productFieldNames = []) {
  const partial = rawLineItemWizard.editColumns || rawLineItemWizard.editTable?.columns;
  const partialKeys =
    partial && typeof partial === 'object'
      ? Object.keys(partial).filter((k) => partial[k] != null && typeof partial[k] === 'object')
      : [];
  const order =
    rawLineItemWizard.editColumnOrder ||
    rawLineItemWizard.editColumnsOrder ||
    (Array.isArray(rawLineItemWizard.edit_columns_order) ? rawLineItemWizard.edit_columns_order : null);
  const orderKeys = Array.isArray(order) ? order : [];
  const pf = Array.isArray(productFieldNames) ? productFieldNames.map((x) => String(x).trim()).filter(Boolean) : [];
  return uniqKeyList(EDIT_BUILTIN_KEYS, pf, partialKeys, orderKeys);
}

/**
 * Extract product field metadata for OM (wizard column palette).
 * @param {*} productConfigPayload — GET /objectconfig/product response `{ data?, fields?, config? }` shapes
 */
/**
 * Resolved wizard block (searchColumns, strings, orders, …).
 * Prefers `config.lineItemWizard`; otherwise migrates from `lineItemWizards.products` or `lineItemWizards.services`.
 * @param {object|null} [configRoot] — parsed object `config` JSON
 */
export function getLineItemWizardRawBlock(configRoot) {
  if (!configRoot || typeof configRoot !== 'object') {
    return {};
  }
  if (configRoot.lineItemWizard && typeof configRoot.lineItemWizard === 'object' && !Array.isArray(configRoot.lineItemWizard)) {
    return configRoot.lineItemWizard;
  }
  const w = configRoot.lineItemWizards;
  if (w && typeof w === 'object' && !Array.isArray(w)) {
    if (w.products && typeof w.products === 'object' && !Array.isArray(w.products)) {
      return w.products;
    }
    if (w.services && typeof w.services === 'object' && !Array.isArray(w.services)) {
      return w.services;
    }
  }
  return {};
}

export function deriveProductColumnsForWizard(productConfigPayload) {
  const cfg = productConfigPayload?.data ?? productConfigPayload;
  const fieldsRaw = cfg?.fields;
  if (!Array.isArray(fieldsRaw)) {
    return { fieldNames: [], labelByFieldName: {}, fields: [] };
  }
  let hidden = [];
  const c = cfg?.config;
  if (typeof c === 'object' && c && Array.isArray(c.hidden_fields)) {
    hidden = c.hidden_fields.map((h) => String(h).trim().toLowerCase());
  } else if (typeof c === 'string') {
    try {
      const p = JSON.parse(c);
      if (p && Array.isArray(p.hidden_fields)) {
        hidden = p.hidden_fields.map((h) => String(h).trim().toLowerCase());
      }
    } catch {
      /* ignore */
    }
  }
  const block = new Set(['id', 'is_deleted', 'createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid']);

  const outNames = [];
  const labelByFieldName = {};
  const fields = [];

  for (const f of fieldsRaw) {
    if (!f || typeof f !== 'object') {
      continue;
    }
    const fn = (f.field_name || '').trim();
    if (!fn || block.has(fn.toLowerCase())) {
      continue;
    }
    if (hidden.includes(fn.toLowerCase())) {
      continue;
    }
    if (f.field_trashed || f.trashed_at || f.is_deleted) {
      continue;
    }
    outNames.push(fn);
    labelByFieldName[fn] = (f.field_label && String(f.field_label).trim()) || humanizeLineItemKey(fn);
    fields.push(f);
  }
  return { fieldNames: uniqKeyList(outNames), labelByFieldName, fields };
}

function mergeColumnUnion(allKeys, partial, defaults, extraLabelByKey = {}) {
  const out = {};
  for (const key of allKeys) {
    const p = partial?.[key];
    const defLbl = defaults[key] || extraLabelByKey[key] || humanizeLineItemKey(key);
    if (p && typeof p === 'object') {
      out[key] = {
        visible: p.visible !== false,
        label: typeof p.label === 'string' && p.label.trim() ? p.label.trim() : defLbl,
      };
    } else {
      out[key] = { visible: true, label: defLbl };
    }
  }
  return out;
}

/**
 * @param {string[]} allKeys
 * @param {string[]|undefined} persistedOrder searchColumnOrder / editColumnOrder from config JSON
 * @param {{ [key: string]: { visible?: boolean, label?: string }}} mergedBeforeOrder
 * @returns {string[]} keys shown in the wizard, in order (remaining keys are available in Object Manager)
 */
export function computeSelectedKeysFromOrder(allKeys, persistedOrder, mergedBeforeOrder) {
  const fallbackSel = allKeys.filter((k) => mergedBeforeOrder[k]?.visible !== false);
  let order = [];
  if (Array.isArray(persistedOrder) && persistedOrder.length > 0) {
    const seen = new Set();
    for (const k of persistedOrder) {
      if (allKeys.includes(k) && !seen.has(k)) {
        seen.add(k);
        order.push(k);
      }
    }
  }
  if (order.length === 0) {
    order = fallbackSel.length ? fallbackSel : [...allKeys];
  }
  return order;
}

function emptyStrings() {
  return {
    modalTitle: null,
    stepSelect: null,
    stepEdit: null,
    searchResultsHeading: null,
    selectedHeading: null,
    editHeading: null,
    searchPlaceholder: null,
    searchingLabel: null,
    noSearchResults: null,
    newSelectedCount: null,
    grandTotal: null,
    addAgain: null,
    add: null,
    remove: null,
    emptySelected: null,
    loadingLineItems: null,
    save: null,
    saving: null,
    next: null,
    cancel: null,
    search: null,
    searching: null,
    footerSelect: null,
    footerEdit: null,
    backButton: null,
  };
}

/**
 * @param {string|object|null|undefined} objectConfigField - raw `object_config.config` value
 * @param {{
 *   productFieldNames?: string[],
 *   productLabelByFieldName?: Record<string,string>,
 *   preferBuiltinColumnPickerDefaults?: boolean,
 * }} [options]
 * When `preferBuiltinColumnPickerDefaults` is true and there is no saved column order, the
 * search/edit **selected** keys default to SEARCH_BUILTIN_KEYS / EDIT_BUILTIN_KEYS intersected
 * with the union — so Object Manager shows extra Product fields under "Available fields" instead of
 * pre-selecting every field (which yielded an empty palette).
 * @returns {object} resolved lineItemWizard (searchColumns, editColumns, strings)
 */
export function parseLineItemWizardConfig(objectConfigField, options = {}) {
  let root = objectConfigField;
  if (typeof root === 'string') {
    try {
      root = JSON.parse(root);
    } catch {
      root = null;
    }
  }
  if (!root || typeof root !== 'object') {
    root = {};
  }

  const raw = getLineItemWizardRawBlock(root);

  const pfFromOpt = options.productFieldNames;
  let productFieldNames = Array.isArray(pfFromOpt) ? pfFromOpt.map((x) => String(x).trim()).filter(Boolean) : [];
  if (
    productFieldNames.length === 0 &&
    Array.isArray(options.productFields) &&
    options.productFields.length > 0
  ) {
    productFieldNames = options.productFields.map((p) => (typeof p === 'string' ? p : p?.field_name)).filter(Boolean);
  }
  const extraLabels =
    options.productLabelByFieldName && typeof options.productLabelByFieldName === 'object'
      ? options.productLabelByFieldName
      : {};

  const strIn = raw.strings && typeof raw.strings === 'object' ? raw.strings : {};
  const strings = emptyStrings();
  for (const k of Object.keys(strings)) {
    if (typeof strIn[k] === 'string' && strIn[k].trim()) {
      strings[k] = strIn[k].trim();
    }
  }

  const searchPartial = raw.searchColumns || raw.searchTable?.columns;
  const editPartial = raw.editColumns || raw.editTable?.columns;

  const searchUnionKeys = collectLineItemSearchUnionKeys(raw, productFieldNames);
  const editUnionKeys = collectLineItemEditUnionKeys(raw, productFieldNames);

  const searchMerged = mergeColumnUnion(searchUnionKeys, searchPartial, DEFAULT_SEARCH_LABELS, extraLabels);
  const editMerged = mergeColumnUnion(editUnionKeys, editPartial, DEFAULT_EDIT_LABELS, extraLabels);

  const persistedSearchOrder =
    raw.searchColumnOrder ||
    raw.searchColumnsOrder ||
    (Array.isArray(raw.search_columns_order) ? raw.search_columns_order : null);

  const persistedEditOrder =
    raw.editColumnOrder ||
    raw.editColumnsOrder ||
    (Array.isArray(raw.edit_columns_order) ? raw.edit_columns_order : null);

  const preferOmPicker =
    options.preferBuiltinColumnPickerDefaults === true;
  const hasPersistedSearchOrder =
    Array.isArray(persistedSearchOrder) && persistedSearchOrder.length > 0;
  const hasPersistedEditOrder =
    Array.isArray(persistedEditOrder) && persistedEditOrder.length > 0;

  let searchSelectedKeys;
  if (hasPersistedSearchOrder) {
    searchSelectedKeys = computeSelectedKeysFromOrder(
      searchUnionKeys,
      persistedSearchOrder,
      searchMerged
    );
  } else if (preferOmPicker) {
    const builtInOrder = SEARCH_BUILTIN_KEYS.filter((k) => searchUnionKeys.includes(k));
    searchSelectedKeys =
      builtInOrder.length > 0
        ? builtInOrder
        : computeSelectedKeysFromOrder(searchUnionKeys, null, searchMerged);
  } else {
    searchSelectedKeys = computeSelectedKeysFromOrder(
      searchUnionKeys,
      null,
      searchMerged
    );
  }

  let editSelectedKeys;
  if (hasPersistedEditOrder) {
    editSelectedKeys = computeSelectedKeysFromOrder(
      editUnionKeys,
      persistedEditOrder,
      editMerged
    );
  } else if (preferOmPicker) {
    const builtInOrder = EDIT_BUILTIN_KEYS.filter((k) => editUnionKeys.includes(k));
    editSelectedKeys =
      builtInOrder.length > 0
        ? builtInOrder
        : computeSelectedKeysFromOrder(editUnionKeys, null, editMerged);
  } else {
    editSelectedKeys = computeSelectedKeysFromOrder(
      editUnionKeys,
      null,
      editMerged
    );
  }

  const searchColumns = {};
  for (const k of searchUnionKeys) {
    const m = searchMerged[k] || {};
    searchColumns[k] = {
      ...m,
      visible: searchSelectedKeys.includes(k),
      label: m.label || DEFAULT_SEARCH_LABELS[k] || extraLabels[k] || humanizeLineItemKey(k),
    };
  }
  const editColumns = {};
  for (const k of editUnionKeys) {
    const m = editMerged[k] || {};
    editColumns[k] = {
      ...m,
      visible: editSelectedKeys.includes(k),
      label: m.label || DEFAULT_EDIT_LABELS[k] || extraLabels[k] || humanizeLineItemKey(k),
    };
  }

  return {
    searchColumns,
    editColumns,
    searchSelectedKeys,
    editSelectedKeys,
    searchUnionKeys,
    editUnionKeys,
    strings,
  };
}

/** Ordered keys displayed in wizard tables (backward compat if omitted). */
export function pickVisibleOrderedSearchKeys(resolvedConfig) {
  const keys = resolvedConfig.searchSelectedKeys;
  const all = Object.keys(resolvedConfig.searchColumns || {});
  if (Array.isArray(keys) && keys.length > 0) {
    return keys.filter((k) => resolvedConfig.searchColumns[k]?.visible !== false);
  }
  return all.filter((key) => resolvedConfig.searchColumns[key]?.visible !== false);
}

export function pickVisibleOrderedEditKeys(resolvedConfig) {
  const keys = resolvedConfig.editSelectedKeys;
  const all = Object.keys(resolvedConfig.editColumns || {});
  if (Array.isArray(keys) && keys.length > 0) {
    return keys.filter((k) => resolvedConfig.editColumns[k]?.visible !== false);
  }
  return all.filter((key) => resolvedConfig.editColumns[key]?.visible !== false);
}

/** String keys for Object Manager (Add products / line item modal) and JSON config. */
export const LINE_ITEM_WIZARD_STRING_KEYS = [
  'modalTitle',
  'stepSelect',
  'stepEdit',
  'searchResultsHeading',
  'selectedHeading',
  'editHeading',
  'searchPlaceholder',
  'searchingLabel',
  'noSearchResults',
  'newSelectedCount',
  'grandTotal',
  'addAgain',
  'add',
  'remove',
  'emptySelected',
  'loadingLineItems',
  'save',
  'saving',
  'next',
  'cancel',
  'search',
  'searching',
  'footerSelect',
  'footerEdit',
  'backButton',
];

const STRING_KEY_LABELS = {
  modalTitle: 'Window title',
  stepSelect: 'Step 1 (breadcrumb)',
  stepEdit: 'Step 2 (breadcrumb)',
  searchResultsHeading: 'Search results section title',
  selectedHeading: 'Selected lines heading (count shown after)',
  editHeading: 'Edit step title',
  searchPlaceholder: 'Search field placeholder',
  searchingLabel: 'Loading text while searching',
  noSearchResults: 'No results message',
  newSelectedCount: 'New count message (rare; leave blank for default)',
  grandTotal: 'Grand total label',
  addAgain: 'Add again button',
  add: 'Add button',
  remove: 'Remove button',
  emptySelected: 'No lines selected message',
  loadingLineItems: 'Loading line items message',
  save: 'Save button',
  saving: 'Saving… button',
  next: 'Next button',
  cancel: 'Cancel button',
  search: 'Search button',
  searching: 'Searching… button',
  footerSelect: 'Footer help (select step)',
  footerEdit: 'Footer help (edit step)',
  backButton: 'Back button',
};

export function getLineItemWizardStringKeyLabel(key) {
  return STRING_KEY_LABELS[key] || key;
}

function parseOptionalBoundInt(v) {
  if (v == null || v === '') {
    return null;
  }
  const n = Math.floor(Number(v));
  return Number.isFinite(n) ? n : null;
}

/**
 * Uses `minQuantity` / `maxQuantity`, or snake_case from API rows (`min_quantity` / `max_quantity`).
 * Omitted/null bounds: min defaults to 1, max unlimited.
 *
 * @param {object|null|undefined} item
 * @returns {{ min: number, max: number | null }}
 */
export function resolveProductQuantityBounds(item) {
  const minRaw = item?.minQuantity ?? item?.min_quantity;
  const maxRaw = item?.maxQuantity ?? item?.max_quantity;
  let min = parseOptionalBoundInt(minRaw);
  let max = parseOptionalBoundInt(maxRaw);
  if (min == null || min < 1) {
    min = 1;
  }
  if (max != null && max < min) {
    max = min;
  }
  return { min, max };
}

/**
 * @param {number|string} raw
 * @param {object|null|undefined} item - line row with product min/max quantity fields
 */
export function clampLineQuantity(raw, item) {
  const { min, max } = resolveProductQuantityBounds(item);
  let n = Math.floor(Number(raw));
  if (!Number.isFinite(n)) {
    n = min;
  }
  if (n < min) {
    n = min;
  }
  if (max != null && n > max) {
    n = max;
  }
  return n;
}

/** Short hint beside quantity fields from product min/max (`null` min ⇒ 1, `null` max ⇒ no limit). */
export function formatQuantityBoundsHint(item) {
  const { min, max } = resolveProductQuantityBounds(item);
  if (max == null) {
    return `Minimum quantity is ${min} (no maximum).`;
  }
  return `Quantity must be between ${min} and ${max}.`;
}

/** Normalize API snake/camel keys onto a line object for persistence in wizard state. */
export function lineItemProductQuantityBounds(row) {
  return {
    minQuantity: parseOptionalBoundInt(row?.min_quantity ?? row?.minQuantity),
    maxQuantity: parseOptionalBoundInt(row?.max_quantity ?? row?.maxQuantity),
  };
}

export function snakeToCamelLineItemKey(snake) {
  return String(snake || '').replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

export function formatLineItemScalarDisplay(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '—';
    }
  }
  const s = String(value);
  return s.length > 240 ? `${s.slice(0, 237)}…` : s;
}

/**
 * Plain text for readonly search-result cells (`action` is interactive — returns empty).
 */
export function getLineItemWizardSearchDisplayedText(product, key) {
  if (!product || key == null) {
    return '';
  }
  if (key === 'action') {
    return '';
  }
  if (key === 'productName') {
    const v =
      product.productName != null && product.productName !== ''
        ? product.productName
        : product.name ?? '';
    return formatLineItemScalarDisplay(v);
  }
  if (key === 'productCode') {
    return formatLineItemScalarDisplay(product.productCode ?? product.product_code);
  }
  if (key === 'productCategory') {
    return formatLineItemScalarDisplay(product.productCategory ?? product.product_category);
  }
  if (key === 'listPrice') {
    const n = Number(product.listPrice);
    if (!Number.isFinite(n)) {
      return '—';
    }
    return Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: product.currency || 'USD',
    }).format(n);
  }
  const camel = snakeToCamelLineItemKey(key);
  const raw = product[key] !== undefined ? product[key] : product[camel];
  if (raw === undefined || raw === null || raw === '') {
    return '—';
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return formatLineItemScalarDisplay(raw);
}

/**
 * Plain text for readonly edit-step cells built-in interactions (quantity, sales price, …) — returns empty string.
 */
export function getLineItemWizardEditDisplayedText(item, key) {
  if (!item || key == null) {
    return '';
  }
  if (['quantity', 'salesPrice', 'discountPercent', 'lineTotal', 'remove'].includes(key)) {
    return '';
  }
  if (key === 'productName') {
    return formatLineItemScalarDisplay(item.productName ?? item.name);
  }
  if (key === 'productCode') {
    return formatLineItemScalarDisplay(item.productCode ?? item.product_code);
  }
  if (key === 'productCategory') {
    return formatLineItemScalarDisplay(item.productCategory ?? item.product_category);
  }
  if (key === 'listPrice') {
    const n = Number(item.listPrice);
    if (!Number.isFinite(n)) {
      return '—';
    }
    return Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: item.currency || 'USD',
    }).format(n);
  }
  const camel = snakeToCamelLineItemKey(key);
  const raw = item[key] !== undefined ? item[key] : item[camel];
  if (raw === undefined || raw === null || raw === '') {
    return '—';
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return formatLineItemScalarDisplay(raw);
}

/**
 * Snapshot product-derived keys from a search row onto draft line state.
 * Omits `action` (UI) and `id` — search payloads often include product/pricebook `id`, which must not
 * be copied to line items: persisted lines use `id` for quote_product / opportunity_product row ids;
 * treating catalog `id` as a line id breaks `!item.id` (new vs saved) and hides items in “Selected”.
 */
export function spreadProductSearchRowOntoLineItem(product) {
  if (!product || typeof product !== 'object') {
    return {};
  }
  const { action: _omitAction, id: _omitCatalogId, ...rest } = product;
  return rest;
}

